import React, { useEffect, useState } from "react";
import axios from "axios";
import "./Categories.css";
import { Pencil, Trash2, Folder, Plus, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import CafeHeader from "../KOTTracker/Header/Header";
import CategoryModal from "./CategoryPops";
import DeleteModal from "./DeleteModal";

const BASE_URL = "https://fliplyn-b-c.fly.dev";
const CAFE_ID = "e7b20008-f754-402a-944c-027bf11d68c0";

export default function Categories() {
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const [categoryData, setCategoryData] = useState({
    name: "",
    description: "",
  });

  const [error, setError] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  /* ================= FETCH ================= */
  const fetchCategories = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${BASE_URL}/cafe/categories/cafe/${CAFE_ID}`
      );
      setCategories(res.data);
      setFilteredCategories(res.data);
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  /* ================= SEARCH ================= */
  useEffect(() => {
    const filtered = categories.filter((cat) =>
      cat.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredCategories(filtered);
  }, [searchTerm, categories]);

  /* ================= NAVIGATION ================= */
  const handleRowClick = (id) => {
    navigate(`/category/${id}`, {
      state: { categoryId: id },
    });
  };

  /* ================= ADD ================= */
  const handleAddClick = () => {
    setIsEdit(false);
    setCategoryData({ name: "", description: "" });
    setError("");
    setModalOpen(true);
  };

  /* ================= EDIT ================= */
  const handleEditClick = (e, cat) => {
    e.stopPropagation(); // Prevent row navigation
    setIsEdit(true);
    setSelectedId(cat.id);
    setCategoryData({
      name: cat.name,
      description: cat.description,
    });
    setError("");
    setModalOpen(true);
  };

  /* ================= SAVE ================= */
  const handleSave = async () => {
    if (!categoryData.name.trim()) {
      setError("Category name is required.");
      return;
    }

    try {
      setError("");

      if (isEdit) {
        await axios.put(
          `${BASE_URL}/cafe/categories/${selectedId}`,
          categoryData
        );
      } else {
        await axios.post(
          `${BASE_URL}/cafe/categories/`,
          {
            ...categoryData,
            cafe_id: CAFE_ID,
          }
        );
      }

      await fetchCategories();
      setModalOpen(false);
    } catch (err) {
      if (err.response?.status === 400) {
        setError(err.response.data.detail);
      } else {
        setError("Something went wrong.");
      }
    }
  };

  /* ================= DELETE ================= */
  const handleDeleteClick = (e, id) => {
    e.stopPropagation(); // Prevent row navigation
    setSelectedId(id);
    setDeleteError("");
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (deleteLoading) return;

    try {
      setDeleteLoading(true);
      setDeleteError("");

      await axios.delete(
        `${BASE_URL}/cafe/categories/${selectedId}`
      );

      await fetchCategories();
      setDeleteOpen(false);
    } catch (err) {
      setDeleteError("Failed to delete category.");
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <>
      <CafeHeader />

      <div className="categories-wrapper">
        <div className="categories-card">
          <div className="categories-header">
            <div className="header-left">
              <div className="icon-box">
                <Folder size={22} />
              </div>
              <h1>Categories</h1>
            </div>

            <div className="header-right">
              <div className="total-pill">
                {filteredCategories.length} Total
              </div>

              <button
                className="add-ctg-btn"
                onClick={handleAddClick}
              >
                <Plus size={16} /> Add Category
              </button>
            </div>
          </div>

          {/* ================= SEARCH BAR ================= */}
          <div className="search-bar-kanchi">
            <Search size={16} className="search-icon" />
            <input
              type="text"
              placeholder="Search categories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="table-header">
            <div>Category Name</div>
            <div>Description</div>
            <div>Created</div>
            <div className="actions-col">Actions</div>
          </div>

          {loading ? (
            <div className="loading-text">Loading...</div>
          ) : filteredCategories.length === 0 ? (
            <div className="empty-text">No categories found</div>
          ) : (
            filteredCategories.map((cat) => (
              <div
                key={cat.id}
                className="table-row clickable-row"
                onClick={() => handleRowClick(cat.id)}
              >
                <div>{cat.name}</div>
                <div>{cat.description}</div>
                <div>
                  {new Date(cat.created_at).toLocaleDateString()}
                </div>
                <div className="actions-col">
                  <button
                    className="edit-btn"
                    onClick={(e) => handleEditClick(e, cat)}
                  >
                    <Pencil size={14} /> Edit
                  </button>

                  <button
                    className="delete-btn"
                    onClick={(e) => handleDeleteClick(e, cat.id)}
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <CategoryModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        title={isEdit ? "Edit Category" : "Add New Category"}
        categoryData={categoryData}
        setCategoryData={setCategoryData}
        error={error}
      />

      <DeleteModal
        isOpen={deleteOpen}
        onClose={() => !deleteLoading && setDeleteOpen(false)}
        onConfirm={confirmDelete}
        loading={deleteLoading}
        error={deleteError}
      />
    </>
  );
}